import { Controller, FieldErrors, Path } from 'react-hook-form';
import { Box, TextField } from '@mui/material';
import {
  isAfter,
  isBefore,
  isValid,
  parse,
  startOfDay,
  subYears,
} from 'date-fns';
import { z } from 'zod';
import { ResponsiveFormRow } from '@ldt/features/orders';
import { FormSectionCard } from '@ldt/features/orders/components/form_section_card';
import { FormDatePicker } from 'src/components/date_picker';
import { SectionHeader } from 'src/components/section_header';
import { FormFieldProps } from 'src/distribution_platforms/features/order_form';
import { LdtOrderDetailFieldsFragment, SpecimenType } from 'src/types';
import { SpecimenTypeUtils } from 'src/utils';

const createLabProcedureDateSchema = (
  specimenType: SpecimenType,
  isRequired: boolean,
) => {
  const label = SpecimenTypeUtils.getSpecimenTypeInputLabel(specimenType);
  const futureMessage = `${label} cannot be in the future`;

  if (isRequired) {
    return z
      .string()
      .min(1, `${label} is required`)
      .refine(isValidPastOrTodayDate, { message: futureMessage });
  }

  return z
    .string()
    .optional()
    .refine((val) => !val?.trim() || isValidPastOrTodayDate(val), {
      message: futureMessage,
    });
};

const createPatientLabAccessionSchema = (isRequired: boolean) =>
  isRequired
    ? z.string().trim().min(1, 'Patient Lab Accession is required')
    : z.string().optional();

const createSlideSamplePartSchema = (isRequired: boolean) =>
  isRequired
    ? z.string().trim().min(1, 'Sample Part is required')
    : z.string().optional();

/**
 * Specimen Information fields for Clinic CS/Sales order forms.
 * Required when editing a QC Pending order; optional on create/submit.
 * Sample Part is only required for certain specimen types.
 */
export const SpecimenInformationFormSchema = (
  specimenType: SpecimenType,
  isRequired: boolean,
) =>
  z.object({
    dateOfProcedure: createLabProcedureDateSchema(specimenType, isRequired),
    patientLabAccession: createPatientLabAccessionSchema(isRequired),
    slideSamplePart: includesSamplePart(specimenType)
      ? createSlideSamplePartSchema(isRequired)
      : z.string().optional(),
  });

export type SpecimenInformationFormData = z.infer<
  ReturnType<typeof SpecimenInformationFormSchema>
>;

export const SPECIMEN_INFORMATION_MISSING_MESSAGE =
  'Specimen information is missing';

type ProcedureDateDobFields = {
  dateOfProcedure?: string | null;
  dateOfBirth?: string | null;
};

/** Returns a blocking error when procedure date is before the patient's DOB. */
export const getProcedureDateBeforeDateOfBirthError = (
  data: ProcedureDateDobFields,
  specimenType: SpecimenType,
): string | null => {
  if (!data.dateOfProcedure?.trim() || !data.dateOfBirth?.trim()) return null;

  const dateOfBirth = parse(data.dateOfBirth.trim(), 'yyyy-MM-dd', new Date());
  const dateOfProcedure = parse(
    data.dateOfProcedure.trim(),
    'yyyy-MM-dd',
    new Date(),
  );
  if (!isValid(dateOfBirth) || !isValid(dateOfProcedure)) return null;

  if (isBefore(startOfDay(dateOfProcedure), startOfDay(dateOfBirth))) {
    const label = SpecimenTypeUtils.getSpecimenTypeInputLabel(specimenType);
    return `${label} cannot be before Date of Birth`;
  }

  return null;
};

export const SpecimenInformationFormSection = <
  T extends SpecimenInformationFormData,
>({
  control,
  errors,
  trigger,
  specimenType,
  required,
  warningsRef,
}: FormFieldProps<T> & {
  specimenType: SpecimenType;
  required: boolean;
  warningsRef?: {
    current: Partial<Record<'dateOfProcedure', string>>;
  };
}) => {
  const dateLabel = SpecimenTypeUtils.getSpecimenTypeInputLabel(specimenType);
  const showSamplePart = includesSamplePart(specimenType);
  const fieldErrors = errors as FieldErrors<SpecimenInformationFormData>;

  return (
    <FormSectionCard header={<SectionHeader title='Specimen Information' />}>
      <Box
        data-testid='specimen-information-section'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        <ResponsiveFormRow>
          <Box
            data-testid='specimen-information-date-of-procedure'
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Controller
              name={'dateOfProcedure' as Path<T>}
              control={control}
              render={({ field }) => (
                <FormDatePicker
                  label={dateLabel}
                  value={field.value}
                  required={required}
                  fullWidth
                  error={!!fieldErrors.dateOfProcedure}
                  helperText={
                    fieldErrors.dateOfProcedure?.message?.toString() ?? ' '
                  }
                  warningText={warningsRef?.current.dateOfProcedure}
                  onChange={(value) => {
                    field.onChange(value ?? '');
                  }}
                  onBlur={() => {
                    field.onBlur();
                    void trigger?.('dateOfProcedure' as Path<T>);
                  }}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              name={'patientLabAccession' as Path<T>}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value}
                  label='Patient Lab Accession'
                  required={required}
                  fullWidth
                  size='small'
                  error={!!fieldErrors.patientLabAccession}
                  helperText={
                    fieldErrors.patientLabAccession?.message?.toString() ?? ' '
                  }
                  onChange={(event) => {
                    field.onChange(event);
                  }}
                  onBlur={() => {
                    field.onBlur();
                    void trigger?.('patientLabAccession' as Path<T>);
                  }}
                />
              )}
            />
          </Box>
        </ResponsiveFormRow>
        {showSamplePart && (
          <ResponsiveFormRow>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Controller
                name={'slideSamplePart' as Path<T>}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value}
                    label='Sample Part'
                    required={required}
                    fullWidth
                    size='small'
                    error={!!fieldErrors.slideSamplePart}
                    helperText={
                      fieldErrors.slideSamplePart?.message?.toString() ?? ' '
                    }
                    onChange={(event) => {
                      field.onChange(event);
                    }}
                    onBlur={() => {
                      field.onBlur();
                      void trigger?.('slideSamplePart' as Path<T>);
                    }}
                  />
                )}
              />
            </Box>
            <Box
              aria-hidden
              sx={{
                flex: 1,
                minWidth: 0,
                display: { xs: 'none', sm: 'block' },
              }}
            />
          </ResponsiveFormRow>
        )}
      </Box>
    </FormSectionCard>
  );
};

const SPECIMEN_TYPES_WITH_SAMPLE_PART = new Set([
  SpecimenType.ProstateBiopsy,
  SpecimenType.MetastaticProstateBiopsy,
]);

const includesSamplePart = (specimenType: SpecimenType): boolean =>
  SPECIMEN_TYPES_WITH_SAMPLE_PART.has(specimenType);

const isValidPastOrTodayDate = (val: string): boolean => {
  if (!val.trim()) return false;
  try {
    const date = parse(val.trim(), 'yyyy-MM-dd', new Date());
    if (!isValid(date)) return false;
    return !isAfter(date, startOfDay(new Date()));
  } catch {
    return false;
  }
};

/**
 * Non-blocking warning when lab procedure date is older than 1 year
 * (same rule as QC Approve dialog).
 */
export const computeLabProcedureDateWarning = (
  dateString: string | null | undefined,
  specimenType: SpecimenType,
): string => {
  if (!dateString?.trim()) return '';
  try {
    const date = parse(dateString.trim(), 'yyyy-MM-dd', new Date());
    if (!isValid(date)) return '';
    const oneYearAgo = subYears(startOfDay(new Date()), 1);
    if (isAfter(oneYearAgo, date)) {
      return `${SpecimenTypeUtils.getSpecimenTypeInputLabel(specimenType)} is older than 1 year`;
    }
  } catch {
    return '';
  }
  return '';
};

export const isSpecimenInformationMissing = (
  order: LdtOrderDetailFieldsFragment,
): boolean => {
  const schema = SpecimenInformationFormSchema(order.specimenType, true);
  const result = schema.safeParse({
    dateOfProcedure: order.labProcedureDatePhi?.trim() ?? '',
    patientLabAccession: order.patientLabAccession?.trim() ?? '',
    slideSamplePart: order.slideSamplePart?.trim() ?? '',
  });
  if (!result.success) return true;

  return (
    getProcedureDateBeforeDateOfBirthError(
      {
        dateOfProcedure: order.labProcedureDatePhi,
        dateOfBirth: order.patient?.dateOfBirthPhi,
      },
      order.specimenType,
    ) !== null
  );
};
